import { useCallback } from 'react';
import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';
import { faClouds } from '@fortawesome/pro-regular-svg-icons';
import { Button, Modal, ModalCloseButton, ModalFooter, ModalHeader, NotificationDispatch, Tab, TabList, TabPanel, Tabs } from '@tryhackme/thm-ui-components';
import { ThemeColors } from '@tryhackme/thm-ui-components/dist/config/theme/theme.types';
import { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { StyledModalIcon, StyledModalTitle } from 'src/common/components/standard-action-modal/standard-action-modal.styles';
import { StyledCircleIcon, StyledIconTag } from 'src/features/access/components/actions-section/access-details/access-details.styles';
import { usePostAwsEnvResetMutation, usePostAwsGenerateEnvironmentMutation } from 'src/features/api/rooms/aws/aws.slice';
import { hasErrorPostData, hasResponseData, isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { SOMETHING_WENT_WRONG } from 'src/common/constants';
import { DEFAULT_AWS_CREDENTIALS } from '../aws-cloud-details.constants';
import { StyledEnvStateAndTag, StylesEnvStateText, StyledCredentialsList, StyledRoomsList } from '../aws-cloud-details.styles';
import { AWSCloudDetailsModalProps, TabOptions, TagType } from '../aws-cloud-details.types';
import { useEnvironmentStatus } from '../use-environment-status';
export const AWSCloudDetailsModal: React.FC<AWSCloudDetailsModalProps> = ({
  open,
  onOpenChange,
  environmentStatus,
  roomsList,
  awsCredentials,
  roomDetails
}) => {
  const theme = useTheme();
  const {
    tagType,
    circleIconColor,
    iconTagText,
    panelText,
    showRoomList,
    showClose,
    showGenerateEnvironment,
    showResetEnvironment
  } = useEnvironmentStatus(environmentStatus);
  const {
    urlLink,
    username,
    accessKeyId,
    secretAccessKey,
    defaultConsolePassword
  } = awsCredentials || DEFAULT_AWS_CREDENTIALS;
  const [postResetEnvAws, {
    isLoading: isEnvResetting
  }] = usePostAwsEnvResetMutation();
  const [postGenerateEnvAws, {
    isLoading: isEnvGenerating
  }] = usePostAwsGenerateEnvironmentMutation();
  const handleGenerateEnvAws = async () => {
    if (!roomDetails?._id) return;
    try {
      const resultGenerating = await postGenerateEnvAws(roomDetails._id).unwrap();
      NotificationDispatch('success', resultGenerating.data);
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : SOMETHING_WENT_WRONG);
    }
  };
  const handleResetEnvAws = useCallback(async () => {
    const resultResetting = await postResetEnvAws();
    if (hasResponseData<string>(resultResetting)) {
      NotificationDispatch('success', resultResetting.data.data);
    } else if (hasErrorPostData(resultResetting)) {
      NotificationDispatch('error', resultResetting.error.data.message);
    } else {
      NotificationDispatch('error', SOMETHING_WENT_WRONG);
    }
  }, [postResetEnvAws]);
  return <Modal open={open} onOpenChange={onOpenChange} isSmall data-sentry-element="Modal" data-sentry-component="AWSCloudDetailsModal" data-sentry-source-file="aws-cloud-details-modal.tsx">
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="aws-cloud-details-modal.tsx">
        <StyledModalIcon icon={faClouds} data-sentry-element="StyledModalIcon" data-sentry-source-file="aws-cloud-details-modal.tsx" />
        <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="aws-cloud-details-modal.tsx">Cloud Information</StyledModalTitle>
      </ModalHeader>

      <Tabs defaultValue={TabOptions.ENVIRONMENT} data-sentry-element="Tabs" data-sentry-source-file="aws-cloud-details-modal.tsx">
        <TabList alignment="left" data-sentry-element="TabList" data-sentry-source-file="aws-cloud-details-modal.tsx">
          <Tab value={TabOptions.ENVIRONMENT} borderbottom={theme.colors.border.light} data-sentry-element="Tab" data-sentry-source-file="aws-cloud-details-modal.tsx">
            {TabOptions.ENVIRONMENT}
          </Tab>
          <Tab value={TabOptions.CREDENTIALS} borderbottom={theme.colors.border.light} data-sentry-element="Tab" data-sentry-source-file="aws-cloud-details-modal.tsx">
            {TabOptions.CREDENTIALS}
          </Tab>
          <TabPanel value={TabOptions.ENVIRONMENT} data-sentry-element="TabPanel" data-sentry-source-file="aws-cloud-details-modal.tsx">
            <StyledEnvStateAndTag data-sentry-element="StyledEnvStateAndTag" data-sentry-source-file="aws-cloud-details-modal.tsx">
              <StylesEnvStateText data-sentry-element="StylesEnvStateText" data-sentry-source-file="aws-cloud-details-modal.tsx">Environment State:</StylesEnvStateText>
              <StyledIconTag type={(tagType as TagType)} data-sentry-element="StyledIconTag" data-sentry-source-file="aws-cloud-details-modal.tsx">
                <StyledCircleIcon type="info" $color={(circleIconColor as keyof Pick<ThemeColors, 'error' | 'success' | 'warning' | 'info'>)} icon={faCircleSmall} data-sentry-element="StyledCircleIcon" data-sentry-source-file="aws-cloud-details-modal.tsx" />
                {iconTagText}
              </StyledIconTag>
            </StyledEnvStateAndTag>
            {!showRoomList && <p>{panelText}</p>}
            {showRoomList && roomsList && roomsList.length ? <>
                <p>You have access to an environment for the following rooms:</p>
                <StyledRoomsList>
                  {roomsList.map(room => <li key={room}>{room}</li>)}
                </StyledRoomsList>
                {panelText && <p>{panelText}</p>}
              </> :
          // Sonarcloud failed && logic, and specifically requested ternary logic here
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>}
          </TabPanel>
          <TabPanel value={TabOptions.CREDENTIALS} data-sentry-element="TabPanel" data-sentry-source-file="aws-cloud-details-modal.tsx">
            <StyledCredentialsList data-sentry-element="StyledCredentialsList" data-sentry-source-file="aws-cloud-details-modal.tsx">
              <li>
                Access AWS Console:
                {urlLink === DEFAULT_AWS_CREDENTIALS.urlLink ? <span>{DEFAULT_AWS_CREDENTIALS.urlLink}</span> : <Link to={urlLink} target="_blank">
                    Go to AWS Console
                  </Link>}
              </li>
              <li>
                Username:<span>{username}</span>
              </li>
              <li>
                Access Key ID:<span>{accessKeyId}</span>
              </li>
              <li>
                Secret Access Key:<span>{secretAccessKey}</span>
              </li>
              <li>
                Default Console Password:<span>{defaultConsolePassword}</span>
              </li>
            </StyledCredentialsList>
          </TabPanel>
        </TabList>
      </Tabs>

      <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="aws-cloud-details-modal.tsx">
        {showClose && <ModalCloseButton text="Close" />}
        {showGenerateEnvironment && <Button isLoading={isEnvGenerating} variant="solid" color="primary" type="button"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={() => handleGenerateEnvAws()}>
            Generate Environment
          </Button>}
        {showResetEnvironment && <Button isLoading={isEnvResetting} variant="solid" color="hint" type="button"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={() => handleResetEnvAws()}>
            Reset Environment
          </Button>}
      </ModalFooter>
    </Modal>;
};